import type { CSSProperties } from "react";
import { chartCrimes, type ChartCrime } from "../data/chartCrimes";
import { Icon } from "./Icons";

const groups = ["Visual error", "Comparison flag"] as const;

export function ChartCrimesView() {
  return (
    <section className="crime-ledger" aria-label="Chart crime audit">
      {groups.map((group) => {
        const findings = chartCrimes.filter((crime) => crime.group === group);
        return (
          <section className="crime-group" key={group}>
            <header className="crime-group__head">
              <h2>{group}</h2>
              <span>{findings.length}</span>
            </header>
            <div className="crime-grid">
              {findings.map((crime) => <CrimeCard crime={crime} key={crime.id} />)}
            </div>
          </section>
        );
      })}
    </section>
  );
}

function CrimeCard({ crime }: { crime: ChartCrime }) {
  return (
    <article className="crime-card">
      <header className="crime-card__head">
        <div>
          <span className="crime-type">{crime.type}</span>
          <h3>{crime.title}</h3>
        </div>
        <div className="crime-meta">
          <strong>{crime.lab}</strong>
          <time dateTime={crime.date}>{crime.date}</time>
        </div>
      </header>

      {crime.images && <CrimeImages crime={crime} />}
      {!crime.images && crime.bars && <CrimeBars crime={crime} />}
      {!crime.images && crime.methods && (
        <div className="crime-methods" aria-label={`${crime.title} methods`}>
          {crime.methods.map((item, index) => (
            <div className={item.flagged ? "crime-method is-flagged" : "crime-method"} key={`${item.label}-${index}`}>
              <span>{item.label}</span>
              <strong>{item.method}</strong>
            </div>
          ))}
        </div>
      )}

      <p className="crime-finding">{crime.finding}</p>
      <footer className="crime-sources">
        {crime.sources.map((source) => (
          <a href={source.url} key={source.url} rel="noreferrer" target="_blank">
            {source.label} <Icon name="external" size={13} />
          </a>
        ))}
      </footer>
    </article>
  );
}

function CrimeImages({ crime }: { crime: ChartCrime }) {
  return (
    <div className="crime-images" aria-label={`${crime.title} source images`}>
      {crime.images?.map((item) => (
        <a
          className="crime-image"
          href={item.sourceUrl ?? item.src}
          key={item.src}
          rel="noreferrer"
          target="_blank"
        >
          <span className="crime-image__frame">
            <img alt={item.alt} decoding="async" loading="lazy" src={item.src} />
          </span>
          <span className="crime-image__caption">
            <strong>{item.label}</strong>
            <span>{item.credit}</span>
          </span>
        </a>
      ))}
    </div>
  );
}

function CrimeBars({ crime }: { crime: ChartCrime }) {
  const scale = crime.scale ?? 100;

  return (
    <div className="crime-bars" aria-label={`${crime.title} values`}>
      {crime.bars?.map((bar) => {
        const drawnValue = bar.drawnValue ?? bar.value;
        const style = {
          "--crime-bar": `${Math.min((drawnValue / scale) * 100, 100)}%`,
          "--crime-correct": `${Math.min((bar.value / scale) * 100, 100)}%`,
        } as CSSProperties;

        return (
          <div className="crime-bar-row" key={bar.label}>
            <span className="crime-bar-row__label">{bar.label}</span>
            <span className="crime-bar-row__track" style={style}>
              <span
                className={[
                  "crime-bar-row__bar",
                  bar.flagged ? "is-flagged" : "",
                  bar.highlighted ? "is-highlighted" : "",
                  bar.leader ? "is-leader" : "",
                ].filter(Boolean).join(" ")}
              />
              {bar.drawnValue !== undefined && bar.drawnValue !== bar.value && (
                <i className="crime-bar-row__correct" aria-hidden="true" />
              )}
            </span>
            <span className="crime-bar-row__value">
              <strong>{bar.displayValue}</strong>
              {bar.note && <small>{bar.note}</small>}
            </span>
          </div>
        );
      })}
    </div>
  );
}
